<template>
  <v-container>
    <div class="priorizacao-ordens">
      <!-- Loading inicial -->
      <v-overlay v-model="carregandoInicial" class="align-center justify-center">
        <v-progress-circular color="primary" indeterminate size="64">
          <template v-slot:default>
            <v-icon size="32">mdi-sort</v-icon>
          </template>
        </v-progress-circular>
        <div class="text-h6 mt-4">Carregando ordens...</div>
      </v-overlay>

      <!-- ✅ TELA INICIAL - Botão para iniciar -->
      <v-card v-if="!dadosCarregados && !carregandoInicial" elevation="8" class="mx-auto mt-8" max-width="600">
        <v-card-text class="text-center pa-12">
          <v-avatar color="primary" size="120" class="mb-6">
            <v-icon color="white" size="80">mdi-sort</v-icon>
          </v-avatar>
          
          <h2 class="text-h4 font-weight-bold mb-3">Priorização de Ordens</h2>
          <p class="text-h6 text-grey-darken-1 mb-8">
            Sistema inteligente de priorização com drag & drop
          </p>

          <div class="d-flex flex-column gap-3">
            <v-btn 
              color="primary" 
              size="x-large" 
              @click="iniciarCarregamento"
              :loading="carregandoOrdens"
              elevation="4"
              rounded="lg"
              class="px-8"
            >
              <v-icon left size="32">mdi-play-circle</v-icon>
              <span class="text-h6">Iniciar Priorização</span>
            </v-btn>

            <v-btn 
              color="grey" 
              size="large" 
              variant="outlined"
              @click="voltarMenu"
              rounded="lg"
              class="px-8"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              Voltar ao Menu
            </v-btn>
          </div>

          <v-alert type="info" variant="tonal" class="mt-8 text-left">
            <div class="text-body-2">
              <v-icon size="20" class="mr-2">mdi-information</v-icon>
              <strong>Dica:</strong> Clique no botão acima para carregar todas as ordens pendentes e começar a priorização.
            </div>
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card v-if="dadosCarregados" elevation="2">
        <v-card-title class="d-flex align-center bg-gradient-primary text-white">
          <v-icon class="mr-2">mdi-sort</v-icon>
          <div>
            <div class="text-h6">Priorização de Ordens de Produção</div>
            <div class="text-subtitle-2 opacity-90">
              Sistema inteligente de priorização com drag & drop
            </div>
          </div>
          <v-spacer />

          <!-- Botões do header -->
          <div class="d-flex align-center gap-2">
            <!-- Botão Voltar ao Menu -->
            <v-btn icon size="small" variant="text" @click="voltarMenu" color="white">
              <v-icon>mdi-arrow-left</v-icon>
              <v-tooltip activator="parent" location="bottom">
                Voltar ao Menu
              </v-tooltip>
            </v-btn>

            <v-divider vertical class="mx-1" style="height: 24px;"></v-divider>

            <!-- Botão de Ajuda -->
            <v-btn icon size="small" variant="text" @click="dialogAjuda = true" color="white">
              <v-icon>mdi-help-circle</v-icon>
              <v-tooltip activator="parent" location="bottom">
                Ajuda e Instruções
              </v-tooltip>
            </v-btn>

            <v-btn icon size="small" variant="text" @click="dialogHistorico = true" color="white">
              <v-icon>mdi-history</v-icon>
              <v-tooltip activator="parent" location="bottom">
                Histórico (Ctrl+H)
              </v-tooltip>
            </v-btn>

            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn icon size="small" variant="text" v-bind="props" color="white">
                  <v-icon>mdi-palette</v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item v-for="tema in temasDisponiveis" :key="tema.nome" @click="aplicarTema(tema)"
                  :class="{ 'v-list-item--active': temaAtual === tema.nome }">
                  <template v-slot:prepend>
                    <v-avatar :color="tema.cor" size="24"></v-avatar>
                  </template>
                  <v-list-item-title>{{ tema.nome }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn color="white" variant="outlined" @click="salvarPriorizacao" :loading="salvando"
              :disabled="!temAlteracoes">
              <v-icon left>mdi-content-save</v-icon>
              Salvar Priorização
              <v-tooltip activator="parent" location="bottom">
                Salvar (Ctrl+S)
              </v-tooltip>
            </v-btn>

            <v-btn icon size="small" variant="text" @click="recarregarOrdens" :loading="carregandoOrdens" color="white">
              <v-icon>mdi-refresh</v-icon>
              <v-tooltip activator="parent" location="bottom">
                Recarregar ordens
              </v-tooltip>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Card de Estatísticas e Controles Unificado -->
          <v-row class="align-center bg-grey-lighten-5 rounded-lg">
            <!-- Estatísticas -->
                 <div class="d-flex align-center mb-4">
                  <v-icon class="mr-2" color="primary">mdi-chart-box</v-icon>
                  <span class="text-h6 font-weight-bold">Estatísticas da Priorização</span>
                </div>

                <v-row v-if="estatisticas" dense>
                  <v-col v-for="(item, index) in [
                    { label: 'Atrasadas', valor: estatisticas.atrasadas, color: 'error' },
                    { label: 'Urgentes', valor: estatisticas.urgentes, color: 'warning' },
                    { label: 'Em Andamento', valor: estatisticas.emAndamento, color: 'info' },
                    { label: 'Progresso Médio', valor: Math.round(estatisticas.progressoMedio) + '%', color: 'success' }

                    , { label: 'Atrasadas', valor: ordensAlteradas.length, color: 'error' }

                  ]" :key="index" cols="6" sm="3" class="text-center">
                    <div :class="`text-h6 font-weight-bold text-${item.color}`">{{ item.valor }}</div>
                    <div class="text-caption text-grey">{{ item.label }}</div>
                  </v-col>
                </v-row>
  
            <!-- Controles -->
            <v-col cols="12" md="4">
              <v-card flat class="pa-2 bg-grey-lighten-5 rounded-lg">
                <v-row dense>

                  <v-col cols="6" class="d-flex flex-column gap-2">
                    <v-btn size="small" variant="outlined" @click="mostrarFiltros = !mostrarFiltros"
                      :color="temFiltrosAtivos ? 'primary' : 'default'" block>
                      <v-icon start size="16">mdi-filter-variant</v-icon>
                      Filtros
                      <v-chip v-if="temFiltrosAtivos" color="primary" size="x-small" class="ml-2">
                        {{ contadorFiltros }}
                      </v-chip>
                    </v-btn>
                  </v-col>

                  <v-col cols="6" class="d-flex flex-column gap-2">
                    <v-btn size="small" variant="outlined" @click="mostrarAcoesRapidas = !mostrarAcoesRapidas" block>
                      <v-icon start size="16">mdi-lightning-bolt</v-icon>
                      Ações Rápidas
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>




          <!-- Drawer de Filtros (apenas mobile) -->
          <v-navigation-drawer v-model="mostrarFiltros" location="end" temporary width="340" class="d-sm-none">
            <v-toolbar flat>
              <v-toolbar-title>Filtros</v-toolbar-title>
              <v-spacer />
              <v-btn icon @click="mostrarFiltros = false" aria-label="Fechar filtros">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-divider />
            <div class="pa-4">
              <v-text-field ref="buscaInput" v-model="filtros.busca" label="Buscar ordem, cliente ou produto"
                prepend-inner-icon="mdi-magnify" variant="outlined" density="compact" clearable
                @input="aplicarFiltros" />
              <v-select class="mt-3" v-model="filtros.status" :items="opcoesStatus" label="Status" variant="outlined"
                density="compact" multiple chips clearable @update:model-value="aplicarFiltros" />
              <v-select class="mt-3" v-model="filtros.prioridade" :items="opcoesPrioridade" label="Faixa de Prioridade"
                variant="outlined" density="compact" clearable @update:model-value="aplicarFiltros" />
              <v-switch class="mt-3" v-model="filtros.apenasAlteradas" label="Apenas Alteradas" color="success"
                density="compact" @update:model-value="aplicarFiltros" />
              <v-btn class="mt-4" size="small" variant="outlined" @click="limparFiltros" :disabled="!temFiltrosAtivos"
                block>
                <v-icon left size="16">mdi-filter-off</v-icon>
                Limpar Filtros
              </v-btn>
            </div>
          </v-navigation-drawer>

          <!-- Filtros (Expansível em telas médias+ | Drawer no mobile) -->
          <v-expand-transition>
            <v-row v-show="mostrarFiltros" class="mb-4 d-none d-sm-flex">
              <v-col cols="12">
                <v-card variant="outlined" class="filtros-card">
                  <v-card-text class="pa-3">
                    <v-row dense>
                      <v-col cols="12" md="3">
                        <v-text-field ref="buscaInput" v-model="filtros.busca" label="Buscar ordem, cliente ou produto"
                          prepend-inner-icon="mdi-magnify" variant="outlined" density="compact" clearable
                          @input="aplicarFiltros">
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" md="3">
                        <v-select v-model="filtros.status" :items="opcoesStatus" label="Status" variant="outlined"
                          density="compact" multiple chips clearable @update:model-value="aplicarFiltros">
                        </v-select>
                      </v-col>

                      <v-col cols="12" md="3">
                        <v-select v-model="filtros.prioridade" :items="opcoesPrioridade" label="Faixa de Prioridade"
                          variant="outlined" density="compact" clearable @update:model-value="aplicarFiltros">
                        </v-select>
                      </v-col>

                      <v-col cols="12" md="3">
                        <div class="d-flex flex-column gap-2">
                          <v-switch v-model="filtros.apenasAlteradas" label="Apenas Alteradas" color="success"
                            density="compact" @update:model-value="aplicarFiltros">
                          </v-switch>

                          <v-btn size="small" variant="outlined" @click="limparFiltros" :disabled="!temFiltrosAtivos"
                            block>
                            <v-icon left size="16">mdi-filter-off</v-icon>
                            Limpar Filtros
                          </v-btn>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-expand-transition>

          <!-- Ações Rápidas (Expansível) -->
          <v-expand-transition>
            <v-row v-show="mostrarAcoesRapidas" class="mb-4">
              <v-col cols="12">
                <v-card variant="outlined" class="acoes-card">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center flex-wrap gap-2">
                      <span class="text-subtitle-2 mr-2">Ações rápidas:</span>

                      <v-btn size="small" variant="outlined" @click="priorizarAtrasadas" :disabled="!temAtrasadas"
                        :loading="processandoAcao">
                        <v-icon left size="16">mdi-clock-alert</v-icon>
                        Priorizar Atrasadas ({{ ordensAtrasadas.length }})
                      </v-btn>

                      <v-btn size="small" variant="outlined" @click="priorizarPorPrazo" :loading="processandoAcao">
                        <v-icon left size="16">mdi-calendar</v-icon>
                        Ordenar por Prazo
                      </v-btn>

                      <v-btn size="small" variant="outlined" @click="priorizarPorProgresso" :loading="processandoAcao">
                        <v-icon left size="16">mdi-chart-line</v-icon>
                        Menor Progresso Primeiro
                      </v-btn>

                      <v-spacer />

                      <v-btn size="small" variant="outlined" @click="resetarPriorizacao" :disabled="!temAlteracoes"
                        :loading="processandoAcao">
                        <v-icon left size="16">mdi-restore</v-icon>
                        Resetar
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-expand-transition>

          <!-- Lista de Ordens -->
          <div class="ordens-container" :class="{ 'smooth-scroll': true }">
            <!-- Zona de drop no topo -->
            <div class="drop-zone drop-zone-top" :class="{ 'drop-zone-active': isDragging && dragOverZone === 'top' }"
              @dragover="handleDragOver($event, 'top')" @dragleave="handleDragLeave" @drop="handleDropZone($event, 0)">
              <v-icon>mdi-arrow-up-bold</v-icon>
              <span>Soltar no topo (Prioridade 1)</span>
            </div>

            <draggable v-model="ordensFiltradasInternas" itemKey="id" handle=".drag-handle" @change="onOrdemAlterada"
              @start="onDragStart" @end="onDragEnd" :animation="300" :scroll-sensitivity="100" :force-fallback="true"
              ghost-class="sortable-ghost" chosen-class="sortable-chosen" drag-class="sortable-drag"
              class="ordens-draggable">

              <template v-slot:item="{ element: ordem, index }">
                <div>
                  <!-- Zona de drop entre itens -->
                  <div v-if="index > 0" class="drop-zone drop-zone-between"
                    :class="{ 'drop-zone-active': isDragging && dragOverZone === `between-${index}` }"
                    @dragover="handleDragOver($event, `between-${index}`)" @dragleave="handleDragLeave"
                    @drop="handleDropZone($event, index)">
                    <div class="drop-line"></div>
                    <div class="drop-indicator">
                      <v-icon size="16">mdi-arrow-down</v-icon>
                      <span class="text-caption">Posição {{ index + 1 }}</span>
                    </div>
                  </div>

                  <v-card class="mb-3 ordem-draggable fade-transition" :class="{
                    'ordem-alterada': ordem.prioridadeAlterada,
                    'ordem-atrasada': ordem.atrasada,
                    'ordem-urgente': ordem.urgente,
                    'ordem-critica': ordem.critica,
                    'ordem-being-dragged': ordem.id === draggedItemId
                  }" elevation="2" @dblclick="abrirDialogPosicao(ordem, index)"
                    @contextmenu.prevent="abrirMenuContexto($event, ordem, index)">

                    <v-card-text class="pa-4">
                      <div class="d-flex align-center">
                        <!-- Handle para arrastar -->
                        <div class="drag-handle-container mr-4">
                          <v-icon class="drag-handle" :color="getPrioridadeCor(index + 1)" size="24" tabindex="0"
                            @mouseenter="showDragHint = ordem.id" @mouseleave="showDragHint = null">
                            mdi-drag-vertical
                          </v-icon>
                          <v-tooltip v-if="showDragHint === ordem.id" activator="parent" location="top">
                            Arraste para reordenar
                          </v-tooltip>
                        </div>

                        <!-- Número da prioridade -->
                        <v-avatar :color="getPrioridadeCor(index + 1)" size="48" class="mr-4 prioridade-avatar">
                          <span class="text-h6 font-weight-bold text-white">
                            {{ index + 1 }}
                          </span>
                          <!-- Indicador de mudança -->
                          <v-icon v-if="ordem.prioridadeAlterada" class="prioridade-changed-indicator pulse-animation"
                            size="16" color="success">
                            mdi-check-circle
                          </v-icon>
                        </v-avatar>

                        <!-- Informações da ordem -->
                        <div class="flex-grow-1">
                          <div class="d-flex align-center mb-2 flex-wrap">
                            <span class="text-h6 font-weight-bold mr-2">
                              Ordem #{{ ordem.id }}
                            </span>

                            <v-chip :color="getCorStatus(ordem.status)" size="small" class="mr-2 ordem-status">
                              {{ formatarStatus(ordem.status) }}
                            </v-chip>

                            <!-- Indicadores de alerta -->
                            <div class="d-flex align-center gap-1">
                              <v-chip v-if="ordem.atrasada" color="error" size="small" variant="outlined">
                                <v-icon left size="16">mdi-clock-alert</v-icon>
                                Atrasada
                              </v-chip>

                              <v-chip v-if="ordem.urgente" color="warning" size="small" variant="outlined">
                                <v-icon left size="16">mdi-fire</v-icon>
                                Urgente
                              </v-chip>

                              <v-chip v-if="ordem.critica" color="error" size="small" variant="outlined">
                                <v-icon left size="16">mdi-alert</v-icon>
                                Crítica
                              </v-chip>

                              <v-chip v-if="ordem.prioridadeAlterada" color="success" size="small" variant="outlined">
                                <v-icon left size="16">mdi-check</v-icon>
                                Alterada
                              </v-chip>
                            </div>
                          </div>

                          <div class="text-body-2 mb-2">
                            <strong>Cliente:</strong> {{ ordem.cliente || 'N/A' }} |
                            <strong>Produto:</strong> {{ ordem.produto || 'N/A' }}
                          </div>

                          <!-- Observação da ordem -->
                          <div v-if="ordem.observacao_prioridade || ordem.observacao_priorizacao"
                            class="text-body-2 mb-2 observacao-container">
                            <v-icon size="16" class="mr-1" color="info">mdi-note-text</v-icon>
                            <strong>Observação:</strong> {{ ordem.observacao_prioridade || ordem.observacao_priorizacao
                            }}
                          </div>

                          <div class="d-flex align-center flex-wrap gap-4">
                            <div class="d-flex align-center">
                              <span class="text-caption mr-2">
                                <strong>Prazo:</strong> {{ formatarDataSegura(ordem.prazo_entrega_formatado ||
                                  ordem.prazo_entrega) }}
                              </span>
                              <span class="text-caption"
                                :class="getDiasRestantesClass(ordem.prazo_entrega_formatado || ordem.prazo_entrega)">
                                ({{ calcularDiasRestantes(ordem.prazo_entrega_formatado || ordem.prazo_entrega) }})
                              </span>
                            </div>

                            <div class="d-flex align-center">
                              <span class="text-caption mr-2">Progresso:</span>
                              <v-progress-linear :model-value="ordem.progresso || 0"
                                :color="getCorProgresso(ordem.progresso)" height="6" rounded class="mr-2"
                                style="width: 100px;" />
                              <span class="text-caption">{{ (ordem.progresso || 0).toFixed(0) }}%</span>
                            </div>

                            <div class="d-flex align-center">
                              <span class="text-caption mr-1">Quantidade:</span>
                              <v-chip size="x-small" color="blue-grey" variant="outlined">
                                {{ ordem.quantidade_total_processos_concluidos || 0 }}/{{
                                  ordem.quantidade_total_processos
                                || 0 }}
                              </v-chip>
                            </div>
                          </div>
                        </div>

                        <!-- Ações -->
                        <div class="ml-4 d-flex flex-column gap-1">
                          <v-btn icon size="small" variant="text" @click="verDetalhesOrdem(ordem)">
                            <v-icon>mdi-eye</v-icon>
                            <v-tooltip activator="parent" location="left">
                              Ver detalhes
                            </v-tooltip>
                          </v-btn>

                          <v-btn icon size="small" variant="text" @click="adicionarObservacao(ordem)">
                            <v-icon>mdi-note-plus</v-icon>
                            <v-tooltip activator="parent" location="left">
                              Adicionar observação....
                            </v-tooltip>
                          </v-btn>

                          <v-menu>
                            <template v-slot:activator="{ props }">
                              <v-btn icon size="small" variant="text" v-bind="props">
                                <v-icon>mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>
                            <v-list>
                              <v-list-item @click="abrirDialogPosicao(ordem, index)">
                                <template v-slot:prepend>
                                  <v-icon>mdi-numeric</v-icon>
                                </template>
                                <v-list-item-title>Definir Posição</v-list-item-title>
                              </v-list-item>
                              <v-divider />
                              <v-list-item @click="moverParaTopo(index)">
                                <template v-slot:prepend>
                                  <v-icon>mdi-arrow-up-bold</v-icon>
                                </template>
                                <v-list-item-title>Mover para o Topo</v-list-item-title>
                              </v-list-item>
                              <v-list-item @click="moverParaFinal(index)">
                                <template v-slot:prepend>
                                  <v-icon>mdi-arrow-down-bold</v-icon>
                                </template>
                                <v-list-item-title>Mover para o Final</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </template>
            </draggable>

            <!-- Zona de drop no final -->
            <div class="drop-zone drop-zone-bottom"
              :class="{ 'drop-zone-active': isDragging && dragOverZone === 'bottom' }"
              @dragover="handleDragOver($event, 'bottom')" @dragleave="handleDragLeave"
              @drop="handleDropZone($event, ordensFiltradasInternas.length)">
              <v-icon>mdi-arrow-down-bold</v-icon>
              <span>Soltar no final (Última prioridade)</span>
            </div>
          </div>

          <!-- Estado Vazio -->
          <div v-if="ordensFiltradasInternas.length === 0 && !carregandoOrdens" class="text-center pa-8">
            <v-icon size="64" color="grey">mdi-sort</v-icon>
            <div class="text-h6 grey--text mt-4 mb-2">
              {{ temFiltrosAtivos ? 'Nenhuma ordem encontrada' : 'Nenhuma ordem para priorizar' }}
            </div>
            <div class="text-body-2 grey--text">
              {{ temFiltrosAtivos ? 'Tente ajustar os filtros' : 'As ordens aparecerão aqui quando estiverem disponíveis'
              }}
            </div>
            <v-btn v-if="temFiltrosAtivos" class="mt-4" variant="outlined" @click="limparFiltros">
              <v-icon left>mdi-filter-off</v-icon>
              Limpar Filtros
            </v-btn>
          </div>

          <!-- Loading de ordens -->
          <div v-if="carregandoOrdens && !carregandoInicial" class="text-center pa-8">
            <v-progress-circular color="primary" indeterminate size="48" />
            <div class="text-body-1 mt-4">Carregando ordens...</div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Dialog de Ajuda -->
      <v-dialog v-model="dialogAjuda" max-width="600px">
        <v-card>
          <v-card-title class="d-flex align-center bg-primary text-white">
            <v-icon class="mr-2">mdi-help-circle</v-icon>
            Ajuda - Como usar o sistema
          </v-card-title>

          <v-card-text class="pa-6">
            <div class="ajuda-content">
              <h3 class="mb-3">🎯 Como priorizar ordens:</h3>
              <ul class="mb-4">
                <li><strong>Arrastar e soltar:</strong> Use o ícone <v-icon size="16"
                    class="mx-1">mdi-drag-vertical</v-icon>
                  para arrastar ordens</li>
                <li><strong>Duplo clique:</strong> Clique duas vezes na ordem para definir posição específica</li>
                <li><strong>Botão direito:</strong> Menu de contexto com ações rápidas</li>
                <li><strong>Zonas de drop:</strong> Aparecem durante o arraste para posicionamento preciso</li>
              </ul>

              <h3 class="mb-3">⌨️ Atalhos de teclado:</h3>
              <ul class="mb-4">
                <li><kbd>Ctrl+S</kbd> - Salvar priorização</li>
                <li><kbd>Ctrl+R</kbd> - Resetar priorização</li>
                <li><kbd>Ctrl+F</kbd> - Focar no campo de busca</li>
                <li><kbd>Ctrl+H</kbd> - Abrir histórico</li>
              </ul>

              <h3 class="mb-3">🔍 Filtros disponíveis:</h3>
              <ul class="mb-4">
                <li><strong>Busca:</strong> Por número da ordem, cliente ou produto</li>
                <li><strong>Status:</strong> Filtrar por status das ordens</li>
                <li><strong>Prioridade:</strong> Por faixa de prioridade</li>
                <li><strong>Alteradas:</strong> Apenas ordens com prioridade modificada</li>
              </ul>

              <h3 class="mb-3">📝 Observações:</h3>
              <ul>
                <li>Use o botão <v-icon size="16">mdi-note-plus</v-icon> para adicionar observações às ordens</li>
                <li>As observações ajudam a documentar o motivo da priorização</li>
                <li>Todas as alterações são registradas no histórico</li>
              </ul>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" @click="dialogAjuda = false">
              Entendi
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog para observação -->
      <v-dialog v-model="dialogObservacao" max-width="500px" persistent>
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-note-plus</v-icon>
            {{ (ordemObservacao && (ordemObservacao.observacao_prioridade || ordemObservacao.observacao_priorizacao)) ?
              'Editar Observação' : 'Adicionar Observação' }}
          </v-card-title>

          <v-card-text class="pa-6">
            <div v-if="ordemObservacao" class="mb-4">
              <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                <strong>Ordem #{{ ordemObservacao.id }}</strong><br>
                <span class="text-caption">{{ ordemObservacao.cliente }} - {{ ordemObservacao.produto }}</span>
              </v-alert>

              <v-textarea v-model="novaObservacao" label="Observação sobre a priorização" variant="outlined" rows="4"
                counter="500" :rules="[v => !v || v.length <= 500 || 'Máximo 500 caracteres']"
                hint="Descreva o motivo da priorização ou informações relevantes" persistent-hint density="compact"
                autofocus>
              </v-textarea>
            </div>
          </v-card-text>

          <v-card-actions class="pa-6 pt-0">
            <v-spacer />
            <v-btn variant="outlined" @click="fecharDialogObservacao">
              Cancelar
            </v-btn>
            <v-btn color="primary" @click="salvarObservacao" :loading="processandoAcao">
              <v-icon left>mdi-content-save</v-icon>
              Salvar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Outros dialogs mantidos iguais... -->
      <!-- Dialog para definir posição específica -->
      <v-dialog v-model="dialogPosicao" max-width="500px" persistent>
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-numeric</v-icon>
            Definir Posição da Ordem
          </v-card-title>

          <v-card-text class="pa-6">
            <div v-if="ordemSelecionada" class="mb-4">
              <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                <strong>Ordem #{{ ordemSelecionada.id }}</strong><br>
                <span class="text-caption">{{ ordemSelecionada.cliente }} - {{ ordemSelecionada.produto }}</span>
              </v-alert>

              <div class="text-body-2 mb-4">
                <strong>Posição atual:</strong> {{ posicaoAtual + 1 }} de {{ ordensFiltradasInternas.length }}
              </div>

              <v-text-field v-model.number="novaPosicao" label="Nova posição" type="number" :min="1"
                :max="ordensFiltradasInternas.length" variant="outlined" density="comfortable" :rules="[validarPosicao]"
                hint="Digite um número de 1 até o total de ordens" persistent-hint @keyup.enter="confirmarNovaPosicao">
                <template v-slot:prepend-inner>
                  <v-icon>mdi-numeric</v-icon>
                </template>
              </v-text-field>

              <!-- Slider visual -->
              <div class="mt-4">
                <v-slider v-model="novaPosicao" :min="1" :max="ordensFiltradasInternas.length" :step="1"
                  show-ticks="always" tick-size="4" color="primary" track-color="grey-lighten-3" thumb-label="always">
                  <template v-slot:thumb-label="{ modelValue }">
                    {{ modelValue }}
                  </template>
                </v-slider>
              </div>

              <!-- Preview da mudança -->
              <v-card variant="outlined" class="pa-3 mt-4 posicao-preview" v-if="novaPosicao !== posicaoAtual + 1">
                <div class="text-subtitle-2 mb-2">
                  <v-icon size="16" class="mr-1">mdi-eye</v-icon>
                  Preview da mudança:
                </div>
                <div class="d-flex align-center gap-2">
                  <v-chip size="small" :color="getPrioridadeCor(posicaoAtual + 1)">
                    {{ posicaoAtual + 1 }}
                  </v-chip>
                  <v-icon>mdi-arrow-right</v-icon>
                  <v-chip size="small" :color="getPrioridadeCor(novaPosicao)">
                    {{ novaPosicao }}
                  </v-chip>
                  <span class="text-caption ml-2">
                    {{ novaPosicao < posicaoAtual + 1 ? 'Subindo' : 'Descendo' }} {{ Math.abs(novaPosicao -
                      (posicaoAtual + 1)) }} posição(ões) </span>
                </div>
              </v-card>
            </div>
          </v-card-text>

          <v-card-actions class="pa-6 pt-0">
            <v-spacer />
            <v-btn variant="outlined" @click="fecharDialogPosicao">
              Cancelar
            </v-btn>
            <v-btn color="primary" @click="confirmarNovaPosicao" :disabled="!novaPosicaoValida"
              :loading="processandoAcao">
              <v-icon left>mdi-check</v-icon>
              Confirmar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog de histórico -->
      <v-dialog v-model="dialogHistorico" max-width="800px">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-history</v-icon>
            Histórico de Priorizações
            <v-spacer />
            <v-btn icon size="small" variant="text" @click="limparHistorico"
              :disabled="historicoAlteracoes.length === 0">
              <v-icon>mdi-delete</v-icon>
              <v-tooltip activator="parent" location="bottom">
                Limpar histórico
              </v-tooltip>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-0" style="max-height: 500px; overflow-y: auto;">
            <div v-if="historicoAlteracoes.length === 0" class="text-center pa-8">
              <v-icon size="64" color="grey">mdi-history</v-icon>
              <div class="text-h6 grey--text mt-4 mb-2">
                Nenhum histórico disponível
              </div>
              <div class="text-body-2 grey--text">
                As alterações aparecerão aqui conforme você usar o sistema
              </div>
            </div>

            <v-timeline v-else density="compact" class="pa-4">
              <v-timeline-item v-for="(item, index) in historicoAlteracoes" :key="index"
                :dot-color="getCorHistorico(item.tipo)" size="small">

                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-subtitle-2 font-weight-medium">
                      {{ item.descricao }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ formatarDataHora(item.timestamp) }} - {{ item.usuario }}
                    </div>
                  </div>

                  <v-chip :color="getCorHistorico(item.tipo)" size="small" variant="outlined">
                    {{ item.quantidade }} ordem{{ item.quantidade !== 1 ? 's' : '' }}
                  </v-chip>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn @click="dialogHistorico = false">Fechar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Menu de contexto -->
      <v-menu v-model="menuContexto" :position-x="menuContextoX" :position-y="menuContextoY" absolute offset-y>
        <v-list v-if="ordemMenuContexto">
          <v-list-item @click="abrirDialogPosicao(ordemMenuContexto, indexMenuContexto)">
            <template v-slot:prepend>
              <v-icon>mdi-numeric</v-icon>
            </template>
            <v-list-item-title>Definir Posição</v-list-item-title>
          </v-list-item>

          <v-list-item @click="adicionarObservacao(ordemMenuContexto)">
            <template v-slot:prepend>
              <v-icon>mdi-note-plus</v-icon>
            </template>
            <v-list-item-title>Adicionar Observação</v-list-item-title>
          </v-list-item>

          <v-divider />

          <v-list-item @click="moverParaTopo(indexMenuContexto)">
            <template v-slot:prepend>
              <v-icon>mdi-arrow-up-bold</v-icon>
            </template>
            <v-list-item-title>Mover para o Topo</v-list-item-title>
          </v-list-item>

          <v-list-item @click="moverParaFinal(indexMenuContexto)">
            <template v-slot:prepend>
              <v-icon>mdi-arrow-down-bold</v-icon>
            </template>
            <v-list-item-title>Mover para o Final</v-list-item-title>
          </v-list-item>

          <v-divider />

          <v-list-item @click="moverParaPosicao(indexMenuContexto, indexMenuContexto - 1)"
            :disabled="indexMenuContexto === 0">
            <template v-slot:prepend>
              <v-icon>mdi-arrow-up</v-icon>
            </template>
            <v-list-item-title>Subir 1 Posição</v-list-item-title>
          </v-list-item>

          <v-list-item @click="moverParaPosicao(indexMenuContexto, indexMenuContexto + 1)"
            :disabled="indexMenuContexto === ordensFiltradasInternas.length - 1">
            <template v-slot:prepend>
              <v-icon>mdi-arrow-down</v-icon>
            </template>
            <v-list-item-title>Descer 1 Posição</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- Resumo das Alterações -->
      <v-card v-if="temAlteracoes" class="mt-4" elevation="2">
        <v-card-title class="bg-success text-white">
          <v-icon class="mr-2">mdi-check-circle</v-icon>
          Resumo das Alterações
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-body-1 font-weight-medium mb-1">
                {{ ordensAlteradas.length }} ordem(ns) com prioridade alterada
              </div>
              <div class="text-caption grey--text">
                As alterações serão salvas no banco de dados
              </div>
            </div>
            <div class="d-flex gap-2">
              <v-btn color="success" @click="salvarPriorizacao" :loading="salvando">
                <v-icon left>mdi-content-save</v-icon>
                Confirmar Alterações
              </v-btn>
              <v-btn variant="outlined" @click="resetarPriorizacao" :loading="processandoAcao">
                <v-icon left>mdi-cancel</v-icon>
                Cancelar
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Dialog de detalhes da ordem -->
      <v-dialog v-model="dialogDetalhes" max-width="900px">
        <v-card v-if="ordemDetalhes">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-eye</v-icon>
            Detalhes da Ordem #{{ ordemDetalhes.id }}
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-list density="compact">
                  <v-list-item>
                    <v-list-item-title>Cliente</v-list-item-title>
                    <v-list-item-subtitle>{{ ordemDetalhes.cliente || 'N/A' }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Produto</v-list-item-title>
                    <v-list-item-subtitle>{{ ordemDetalhes.produto || 'N/A' }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Status</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip :color="getCorStatus(ordemDetalhes.status)" size="small">
                        {{ formatarStatus(ordemDetalhes.status) }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-col>
              <v-col cols="12" md="6">
                <v-list density="compact">
                  <v-list-item>
                    <v-list-item-title>Prazo de Entrega</v-list-item-title>
                    <v-list-item-subtitle>{{ formatarDataSegura(ordemDetalhes.prazo_entrega_formatado ||
                      ordemDetalhes.prazo_entrega) }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Progresso</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-progress-linear :model-value="ordemDetalhes.progresso || 0"
                        :color="getCorProgresso(ordemDetalhes.progresso)" height="8" rounded class="mt-2" />
                      <span class="text-caption">{{ (ordemDetalhes.progresso || 0).toFixed(0) }}%</span>
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Prioridade Atual</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip :color="getPrioridadeCor(ordemDetalhes.prioridade || 999)" size="small">
                        {{ ordemDetalhes.prioridade || 'Não definida' }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>

            <!-- Observação se existir -->
            <v-row v-if="ordemDetalhes.observacao_priorizacao">
              <v-col cols="12">
                <v-alert type="info" variant="tonal">
                  <div class="text-subtitle-2 mb-2">
                    <v-icon class="mr-1">mdi-note-text</v-icon>
                    Observação da Priorização:
                  </div>
                  <div>{{ ordemDetalhes.observacao_priorizacao }}</div>
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn @click="dialogDetalhes = false">Fechar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar para feedback -->
      <v-snackbar v-model="mostrarNotificacao" :color="tipoNotificacao" :timeout="4000" location="top right">
        {{ mensagemNotificacao }}
        <template v-slot:actions>
          <v-btn variant="text" @click="mostrarNotificacao = false">
            Fechar
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </v-container>
</template>



<script>
import { useUserStore } from "@/store/userStore";
import draggable from 'vuedraggable';
import { producaoService } from '@/services/producaoService';

export default {
  name: 'PriorizacaoOrdens',
  components: {
    draggable
  },
  data() {
    return {
      // Estados de loading
      dadosCarregados: false, // ✅ NOVO: controla se dados já foram carregados
      carregandoInicial: false, // ✅ ALTERADO: inicia como false
      carregandoOrdens: false,
      salvando: false,
      processandoAcao: false,

      // Dados das ordens
      ordensOriginais: [],
      ordensOrdenadas: [],
      ordensFiltradasInternas: [], // Array interno para o draggable
      prioridadeOriginal: [],

      // Sistema de drag and drop
      isDragging: false,
      draggedItemId: null,
      dragOverZone: null,
      showDragHint: null,

      // Controles de interface
      mostrarFiltros: false,
      mostrarAcoesRapidas: false,

      // Filtros
      filtros: {
        busca: '',
        status: [],
        prioridade: null,
        apenasAlteradas: false
      },

      // Dialogs
      dialogAjuda: false,
      dialogPosicao: false,
      ordemSelecionada: null,
      posicaoAtual: 0,
      novaPosicao: 1,

      dialogHistorico: false,
      historicoAlteracoes: [],

      dialogDetalhes: false,
      ordemDetalhes: null,

      dialogObservacao: false,
      ordemObservacao: null,
      novaObservacao: '',

      // Menu de contexto
      menuContexto: false,
      menuContextoX: 0,
      menuContextoY: 0,
      ordemMenuContexto: null,
      indexMenuContexto: 0,

      // Temas
      temaAtual: 'Padrão',
      temasDisponiveis: [
        { nome: 'Padrão', cor: 'blue', classe: 'tema-padrao' },
        { nome: 'Escuro', cor: 'grey-darken-3', classe: 'tema-escuro' },
        { nome: 'Colorido', cor: 'purple', classe: 'tema-colorido' },
        { nome: 'Minimalista', cor: 'blue-grey', classe: 'tema-minimal' }
      ],

      // Sistema de notificações
      mostrarNotificacao: false,
      mensagemNotificacao: '',
      tipoNotificacao: 'success',

      // Auto-save
      autoSaveInterval: null,
      autoSaveAtivo: false,

      // Opções para filtros e selects
      opcoesStatus: [
        { title: 'Pendente', value: 'pendente' },
        { title: 'Em Andamento', value: 'em_andamento' },
        { title: 'Iniciada', value: 'iniciada' },
        { title: 'Concluída', value: 'concluida' },
        { title: 'Cancelada', value: 'cancelada' }
      ],

      opcoesPrioridade: [
        { title: 'Alta (1-3)', value: 'alta' },
        { title: 'Média (4-6)', value: 'media' },
        { title: 'Baixa (7-10)', value: 'baixa' },
        { title: 'Muito Baixa (11+)', value: 'muito-baixa' }
      ]
    }
  },

  computed: {
    ordensAlteradas() {
      return this.ordensOrdenadas.filter(ordem => ordem.prioridadeAlterada);
    },

    temAlteracoes() {
      return this.ordensAlteradas.length > 0;
    },

    ordensAtrasadas() {
      return this.ordensOrdenadas.filter(ordem => ordem.atrasada);
    },

    temAtrasadas() {
      return this.ordensAtrasadas.length > 0;
    },

    novaPosicaoValida() {
      return this.novaPosicao >= 1 &&
        this.novaPosicao <= this.ordensFiltradasInternas.length &&
        this.novaPosicao !== this.posicaoAtual + 1;
    },

    estatisticas() {
      if (!this.ordensOrdenadas.length) return null;

      return {
        atrasadas: this.ordensOrdenadas.filter(o => o.atrasada).length,
        urgentes: this.ordensOrdenadas.filter(o => o.urgente).length,
        emAndamento: this.ordensOrdenadas.filter(o => o.status === 'em_andamento').length,
        progressoMedio: this.ordensOrdenadas.reduce((acc, o) => acc + (o.progresso || 0), 0) / this.ordensOrdenadas.length
      };
    },

    temFiltrosAtivos() {
      return !!(this.filtros.busca ||
        this.filtros.status.length > 0 ||
        this.filtros.prioridade ||
        this.filtros.apenasAlteradas);
    },

    contadorFiltros() {
      let count = 0;
      if (this.filtros.busca) count++;
      if (this.filtros.status.length > 0) count++;
      if (this.filtros.prioridade) count++;
      if (this.filtros.apenasAlteradas) count++;
      return count;
    }
  },

  watch: {
    // Sincronizar filtros com array interno
    ordensOrdenadas: {
      handler() {
        this.aplicarFiltros();
      },
      deep: true
    },

    filtros: {
      handler() {
        this.aplicarFiltros();
      },
      deep: true
    }
  },

  async mounted() {
    // ✅ NÃO CARREGA MAIS AUTOMATICAMENTE - usuário precisa clicar no botão
    
    // Adicionar listeners para atalhos de teclado
    document.addEventListener('keydown', this.handleKeyboardShortcuts);

    // Carregar histórico do localStorage
    this.carregarHistorico();

    // Carregar tema salvo
    this.carregarTema();
    
    console.log('🎯 Componente montado - aguardando usuário clicar em "Iniciar"');
  },

  beforeUnmount() {
    // Remover listeners
    document.removeEventListener('keydown', this.handleKeyboardShortcuts);

    // Parar auto-save
    this.pararAutoSave();

    // Salvar histórico
    this.salvarHistorico();
  },

  methods: {
    // ========== MÉTODOS DE CARREGAMENTO (USANDO API REAL) ==========
    
    // ✅ MÉTODO PARA INICIAR O CARREGAMENTO (chamado pelo botão)
    async iniciarCarregamento() {
      this.carregandoInicial = true;
      await this.carregarOrdens();
      this.dadosCarregados = true;
      this.carregandoInicial = false;
      
      // Iniciar auto-save se configurado
      if (this.autoSaveAtivo) {
        this.iniciarAutoSave();
      }
    },

    // 🔙 MÉTODO PARA VOLTAR AO MENU
    voltarMenu() {
      this.$router.push({ name: 'MenuDefault' });
      console.log('🏠 Voltando ao menu principal');
    },
    
    async carregarOrdens() {
      this.carregandoOrdens = true;
      try {
        console.log('🔄 Carregando ordens da API...');

        // Usar a mesma chamada do dashboard
        const response = await producaoService.buscarTodasOrdens();

        if (response.status === 'sucesso' && response.ordens) {
          // Processar ordens igual ao dashboard
          this.ordensOriginais = response.ordens.map(ordem => ({
            ...ordem,
            progresso: this.calcularProgresso(ordem),
            urgente: this.verificarUrgencia(ordem),
            critica: this.verificarCriticidade(ordem),
            atrasada: this.verificarAtraso(ordem)
          }));

          // Filtrar apenas ordens pendentes e em andamento para priorização
          const ordensParaPriorizacao = this.ordensOriginais
            .filter(ordem => ordem.status === 'pendente' || ordem.status === 'em_andamento')
            .sort((a, b) => {
              // Ordenar por prioridade atual ou urgência
              if (a.urgente && !b.urgente) return -1;
              if (!a.urgente && b.urgente) return 1;
              if (a.atrasada && !b.atrasada) return -1;
              if (!a.atrasada && b.atrasada) return 1;
              return new Date(a.prazo_entrega_formatado || a.prazo_entrega) - new Date(b.prazo_entrega_formatado || b.prazo_entrega);
            });

          this.inicializarOrdens(ordensParaPriorizacao);

          console.log('✅ Ordens carregadas:', this.ordensOriginais.length);
          console.log('✅ Ordens para priorização:', ordensParaPriorizacao.length);

          this.mostrarNotificacaoSucesso(`${ordensParaPriorizacao.length} ordens carregadas para priorização!`);
        } else {
          console.warn('⚠️ Nenhuma ordem encontrada');
          this.ordensOriginais = [];
          this.ordensOrdenadas = [];
          this.mostrarNotificacaoInfo('Nenhuma ordem encontrada para priorização');
        }
      } catch (error) {
        console.error('❌ Erro ao carregar ordens:', error);
        this.mostrarNotificacaoErro('Erro ao carregar ordens de produção');

        // Fallback para dados de exemplo em caso de erro
        this.ordensOriginais = this.gerarDadosExemplo();
        this.inicializarOrdens(this.ordensOriginais);
      } finally {
        this.carregandoOrdens = false;
      }
    },

    async recarregarOrdens() {
      await this.carregarOrdens();
      this.mostrarNotificacaoInfo('Ordens recarregadas com sucesso');
    },

    // ========== MÉTODOS DE CÁLCULO (IGUAIS AO DASHBOARD) ==========
    calcularProgresso(ordem) {
      if (!ordem.quantidade_total_processos || ordem.quantidade_total_processos === 0) {
        return 0;
      }

      const processosCompletos = ordem.quantidade_total_processos_concluidos || 0;
      const progressoCalculado = (processosCompletos / ordem.quantidade_total_processos) * 100;

      return Math.min(Math.max(progressoCalculado, 0), 100);
    },

    verificarUrgencia(ordem) {
      if (!ordem.prazo_entrega_formatado && !ordem.prazo_entrega) return false;

      const hoje = new Date();
      const prazo = new Date(ordem.prazo_entrega_formatado || ordem.prazo_entrega);

      // Verificar se a data é válida
      if (isNaN(prazo.getTime())) return false;

      const diffTime = prazo - hoje;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Considera urgente se faltam 3 dias ou menos
      return diffDays <= 3 && diffDays >= 0;
    },

    verificarCriticidade(ordem) {
      // Considera crítica se tem alta prioridade E está atrasada
      const prioridade = ordem.prioridade || 999;
      const atrasada = this.verificarAtraso(ordem);

      return prioridade <= 3 && atrasada;
    },

    verificarAtraso(ordem) {
      if (!ordem.prazo_entrega_formatado && !ordem.prazo_entrega) return false;

      const hoje = new Date();
      const prazo = new Date(ordem.prazo_entrega_formatado || ordem.prazo_entrega);

      // Verificar se a data é válida
      if (isNaN(prazo.getTime())) return false;

      const progresso = this.calcularProgresso(ordem);

      // Considera atrasada se passou do prazo e não está 100% completa
      return prazo < hoje && progresso < 100;
    },

    gerarDadosExemplo() {
      // Dados de exemplo para desenvolvimento (fallback)
      const exemplos = [];
      const clientes = ['Empresa A', 'Empresa B', 'Empresa C', 'Empresa D', 'Empresa E'];
      const produtos = ['Produto X', 'Produto Y', 'Produto Z', 'Produto W', 'Produto V'];
      const status = ['pendente', 'em_andamento'];

      for (let i = 1; i <= 15; i++) {
        const prazo = new Date();
        prazo.setDate(prazo.getDate() + Math.floor(Math.random() * 30) - 10);

        const quantidadeTotal = Math.floor(Math.random() * 50) + 10;
        const quantidadeConcluida = Math.floor(Math.random() * quantidadeTotal);

        exemplos.push({
          id: i,
          cliente: clientes[Math.floor(Math.random() * clientes.length)],
          produto: produtos[Math.floor(Math.random() * produtos.length)],
          status: status[Math.floor(Math.random() * status.length)],
          prazo_entrega: prazo.toISOString().split('T')[0],
          prazo_entrega_formatado: prazo.toISOString().split('T')[0],
          prioridade: i,
          quantidade_total_processos: quantidadeTotal,
          quantidade_total_processos_concluidos: quantidadeConcluida,
          prioridadeAlterada: false
        });
      }

      return exemplos;
    },

    inicializarOrdens(ordens) {
      // Processar dados das ordens
      const ordensProcessadas = ordens.map(ordem => ({
        ...ordem,
        prioridadeAlterada: false,
        observacao_prioridade: ordem.observacao_prioridade || ordem.observacao_priorizacao || null
      }));

      // Ordenar por prioridade existente ou por ID
      this.ordensOrdenadas = [...ordensProcessadas].sort((a, b) => {
        const prioridadeA = a.prioridade || 999;
        const prioridadeB = b.prioridade || 999;

        if (prioridadeA === prioridadeB) {
          return a.id - b.id;
        }

        return prioridadeA - prioridadeB;
      });

      // Salvar ordem original para comparação
      this.prioridadeOriginal = this.ordensOrdenadas.map(o => o.id);

      // Aplicar filtros iniciais
      this.aplicarFiltros();

      // Forçar verificação inicial de alterações
      this.onOrdemAlterada();
    },

    // ========== MÉTODOS DE FILTROS ==========
    aplicarFiltros() {
      let ordens = [...this.ordensOrdenadas];

      // Aplicar filtros
      if (this.filtros.busca) {
        const termo = this.filtros.busca.toLowerCase();
        ordens = ordens.filter(ordem =>
          ordem.id.toString().includes(termo) ||
          (ordem.cliente && ordem.cliente.toLowerCase().includes(termo)) ||
          (ordem.produto && ordem.produto.toLowerCase().includes(termo))
        );
      }

      if (this.filtros.status.length > 0) {
        ordens = ordens.filter(ordem => this.filtros.status.includes(ordem.status));
      }

      if (this.filtros.prioridade) {
        ordens = ordens.filter(ordem => {
          const pos = this.ordensOrdenadas.findIndex(o => o.id === ordem.id) + 1;
          switch (this.filtros.prioridade) {
            case 'alta': return pos <= 3;
            case 'media': return pos >= 4 && pos <= 6;
            case 'baixa': return pos >= 7 && pos <= 10;
            case 'muito-baixa': return pos > 10;
            default: return true;
          }
        });
      }

      if (this.filtros.apenasAlteradas) {
        ordens = ordens.filter(ordem => ordem.prioridadeAlterada);
      }

      // Atualizar array interno
      this.ordensFiltradasInternas = ordens;
    },

    limparFiltros() {
      this.filtros = {
        busca: '',
        status: [],
        prioridade: null,
        apenasAlteradas: false
      };
      this.mostrarNotificacaoInfo('Filtros limpos');
    },

    // ========== MÉTODOS DE DRAG AND DROP ==========
    onDragStart(evt) {
      this.isDragging = true;
      this.draggedItemId = this.ordensFiltradasInternas[evt.oldIndex]?.id;
      document.body.style.cursor = 'grabbing';
      document.body.classList.add('is-dragging');
    },

    onDragEnd(evt) {
      this.isDragging = false;
      this.draggedItemId = null;
      this.dragOverZone = null;
      document.body.style.cursor = '';
      document.body.classList.remove('is-dragging');
    },

    handleDragOver(event, zone) {
      event.preventDefault();
      this.dragOverZone = zone;
    },

    handleDragLeave() {
      this.dragOverZone = null;
    },

    handleDropZone(event, targetIndex) {
      event.preventDefault();
      this.dragOverZone = null;

      if (!this.draggedItemId) return;

      const draggedIndex = this.ordensFiltradasInternas.findIndex(o => o.id === this.draggedItemId);
      if (draggedIndex === -1) return;

      // Remove o item da posição atual
      const draggedItem = this.ordensFiltradasInternas.splice(draggedIndex, 1)[0];

      // Insere na nova posição
      const insertIndex = targetIndex > draggedIndex ? targetIndex - 1 : targetIndex;
      this.ordensFiltradasInternas.splice(insertIndex, 0, draggedItem);

      // Atualizar ordem principal
      this.sincronizarOrdemPrincipal();
      this.onOrdemAlterada();

      this.adicionarHistorico('manual', `Ordem #${draggedItem.id} movida para posição ${insertIndex + 1}`, 1);
      this.mostrarNotificacaoInfo(`Ordem #${draggedItem.id} movida para posição ${insertIndex + 1}`);
    },

    sincronizarOrdemPrincipal() {
      // Sincronizar a ordem principal com a ordem visível filtrada
      const ordensNaoVisiveis = this.ordensOrdenadas.filter(ordem =>
        !this.ordensFiltradasInternas.find(v => v.id === ordem.id)
      );

      this.ordensOrdenadas = [...this.ordensFiltradasInternas, ...ordensNaoVisiveis];
    },

    onOrdemAlterada() {
      this.ordensOrdenadas.forEach((ordem, index) => {
        const posicaoOriginal = this.prioridadeOriginal.indexOf(ordem.id);
        ordem.prioridadeAlterada = posicaoOriginal !== index;
        ordem.novaPrioridade = index + 1;
      });

      // Forçar reatividade do computed
      this.$forceUpdate();
    },

    // ========== MÉTODOS DE POSICIONAMENTO ==========
    abrirDialogPosicao(ordem, index) {
      this.ordemSelecionada = ordem;
      this.posicaoAtual = index;
      this.novaPosicao = index + 1;
      this.dialogPosicao = true;
      this.menuContexto = false;
    },

    fecharDialogPosicao() {
      this.dialogPosicao = false;
      this.ordemSelecionada = null;
      this.posicaoAtual = 0;
      this.novaPosicao = 1;
    },

    async confirmarNovaPosicao() {
      if (!this.novaPosicaoValida || !this.ordemSelecionada) return;

      this.processandoAcao = true;
      try {
        const novoIndex = this.novaPosicao - 1;
        this.moverParaPosicao(this.posicaoAtual, novoIndex);

        this.adicionarHistorico(
          'manual',
          `Ordem #${this.ordemSelecionada.id} movida para posição ${this.novaPosicao}`,
          1
        );

        this.mostrarNotificacaoSucesso(
          `Ordem #${this.ordemSelecionada.id} movida para posição ${this.novaPosicao}`
        );

        this.fecharDialogPosicao();
      } finally {
        this.processandoAcao = false;
      }
    },

    validarPosicao(value) {
      if (!value || value < 1 || value > this.ordensFiltradasInternas.length) {
        return `Posição deve estar entre 1 e ${this.ordensFiltradasInternas.length}`;
      }
      return true;
    },

    moverParaPosicao(fromIndex, toIndex) {
      if (fromIndex === toIndex || toIndex < 0 || toIndex >= this.ordensFiltradasInternas.length) {
        return;
      }

      const item = this.ordensFiltradasInternas.splice(fromIndex, 1)[0];
      this.ordensFiltradasInternas.splice(toIndex, 0, item);
      this.sincronizarOrdemPrincipal();
      this.onOrdemAlterada();
    },

    moverParaTopo(index) {
      this.moverParaPosicao(index, 0);
      this.menuContexto = false;
      this.adicionarHistorico('manual', `Ordem movida para o topo`, 1);
    },

    moverParaFinal(index) {
      this.moverParaPosicao(index, this.ordensFiltradasInternas.length - 1);
      this.menuContexto = false;
      this.adicionarHistorico('manual', `Ordem movida para o final`, 1);
    },

    // ========== MÉTODOS DE OBSERVAÇÃO ==========
    adicionarObservacao(ordem) {
      this.ordemObservacao = ordem;
      // Prefill usando novo campo com fallback ao legado
      this.novaObservacao = ordem.observacao_prioridade || ordem.observacao_priorizacao || '';
      this.dialogObservacao = true;
      this.menuContexto = false;
    },

    fecharDialogObservacao() {
      this.dialogObservacao = false;
      this.ordemObservacao = null;
      this.novaObservacao = '';
    },

    async salvarObservacao() {
      if (!this.ordemObservacao) return;

      this.processandoAcao = true;
      try {
        // Salvar observação diretamente na API
        const response = await producaoService.salvarObservacaoPrioridade(
          this.ordemObservacao.id,
          this.novaObservacao
        );

        if (response.status === 'sucesso') {
          // Atualizar a observação na ordem
          const ordemIndex = this.ordensOrdenadas.findIndex(o => o.id === this.ordemObservacao.id);
          if (ordemIndex !== -1) {
            this.ordensOrdenadas[ordemIndex].observacao_prioridade = this.novaObservacao;
            this.ordensOrdenadas[ordemIndex].observacao_priorizacao = this.novaObservacao;
          }

          // Atualizar também no array filtrado
          const ordemFiltradaIndex = this.ordensFiltradasInternas.findIndex(o => o.id === this.ordemObservacao.id);
          if (ordemFiltradaIndex !== -1) {
            this.ordensFiltradasInternas[ordemFiltradaIndex].observacao_prioridade = this.novaObservacao;
            this.ordensFiltradasInternas[ordemFiltradaIndex].observacao_priorizacao = this.novaObservacao;
          }

          this.adicionarHistorico(
            'manual',
            `Observação ${this.novaObservacao ? 'adicionada' : 'removida'} na ordem #${this.ordemObservacao.id}`,
            1
          );

          this.mostrarNotificacaoSucesso(
            `Observação ${this.novaObservacao ? 'salva' : 'removida'} com sucesso!`
          );
        } else {
          throw new Error(response.mensagem || 'Erro ao salvar observação');
        }

        this.fecharDialogObservacao();
      } catch (error) {
        console.error('❌ Erro ao salvar observação:', error);
        this.mostrarNotificacaoErro(
          error.message || 'Erro ao salvar observação'
        );
      } finally {
        this.processandoAcao = false;
      }
    },

    // ========== MÉTODOS DE MENU DE CONTEXTO ==========
    abrirMenuContexto(event, ordem, index) {
      event.preventDefault();
      this.ordemMenuContexto = ordem;
      this.indexMenuContexto = index;
      this.menuContextoX = event.clientX;
      this.menuContextoY = event.clientY;
      this.menuContexto = true;
    },

    // ========== MÉTODOS DE AÇÕES RÁPIDAS ==========
    async priorizarAtrasadas() {
      this.processandoAcao = true;
      try {
        const atrasadas = this.ordensOrdenadas.filter(o => o.atrasada);
        const naoAtrasadas = this.ordensOrdenadas.filter(o => !o.atrasada);

        atrasadas.sort((a, b) => {
          const dataA = new Date(a.prazo_entrega_formatado || a.prazo_entrega);
          const dataB = new Date(b.prazo_entrega_formatado || b.prazo_entrega);
          return dataA - dataB;
        });

        this.ordensOrdenadas = [...atrasadas, ...naoAtrasadas];
        this.aplicarFiltros();
        this.onOrdemAlterada();

        this.adicionarHistorico(
          'automatico',
          'Ordens atrasadas priorizadas automaticamente',
          atrasadas.length
        );

        this.mostrarNotificacaoInfo(
          `${atrasadas.length} ordem(ns) atrasada(s) priorizadas`
        );
      } finally {
        this.processandoAcao = false;
      }
    },

    async priorizarPorPrazo() {
      this.processandoAcao = true;
      try {
        this.ordensOrdenadas.sort((a, b) => {
          const dataA = new Date(a.prazo_entrega_formatado || a.prazo_entrega || '9999-12-31');
          const dataB = new Date(b.prazo_entrega_formatado || b.prazo_entrega || '9999-12-31');
          return dataA - dataB;
        });

        this.aplicarFiltros();
        this.onOrdemAlterada();

        this.adicionarHistorico('automatico', 'Ordens ordenadas por prazo de entrega');
        this.mostrarNotificacaoInfo('Ordens ordenadas por prazo de entrega');
      } finally {
        this.processandoAcao = false;
      }
    },

    async priorizarPorProgresso() {
      this.processandoAcao = true;
      try {
        this.ordensOrdenadas.sort((a, b) => {
          const progressoA = a.progresso || 0;
          const progressoB = b.progresso || 0;
          return progressoA - progressoB;
        });

        this.aplicarFiltros();
        this.onOrdemAlterada();

        this.adicionarHistorico('automatico', 'Ordens ordenadas por progresso (menor primeiro)');
        this.mostrarNotificacaoInfo('Ordens ordenadas por progresso (menor primeiro)');
      } finally {
        this.processandoAcao = false;
      }
    },

    async resetarPriorizacao() {
      this.processandoAcao = true;
      try {
        this.inicializarOrdens(this.ordensOriginais.filter(ordem =>
          ordem.status === 'pendente' || ordem.status === 'em_andamento'
        ));
        this.adicionarHistorico('reset', 'Priorização resetada para estado original');
        this.mostrarNotificacaoInfo('Priorização resetada para o estado original');
      } finally {
        this.processandoAcao = false;
      }
    },

    // ========== MÉTODOS DE SALVAMENTO ==========
    async salvarPriorizacao() {
      if (!this.temAlteracoes) {
        this.mostrarNotificacaoInfo('Nenhuma alteração para salvar');
        return;
      }

      this.salvando = true;
      try {
        const priorizacao = this.ordensOrdenadas.map((ordem, index) => ({
          id: ordem.id,
          prioridade: index + 1,
          observacao_prioridade: ordem.observacao_prioridade || ordem.observacao_priorizacao || null
        }));

        const userStore = useUserStore();
        const usuarioCadastroId = userStore.objetoUsuario?.id_funcionario
          ? parseInt(userStore.objetoUsuario.id_funcionario, 10)
          : 0;

        const usuario = usuarioCadastroId || 0;
        const observacao = 'Reordenação manual via sistema de priorização';

        console.log('💾 Salvando priorização:', {
          priorizacao: priorizacao.length,
          usuario,
          observacao
        });

        // Usar o serviço de produção para salvar
        const response = await producaoService.salvarPriorizacaoOrdens(
          priorizacao,
          usuario,
          observacao
        );

        if (response.status === 'sucesso') {
          // Atualizar prioridade original após salvar
          this.prioridadeOriginal = this.ordensOrdenadas.map(o => o.id);
          this.ordensOrdenadas.forEach(ordem => {
            ordem.prioridadeAlterada = false;
            ordem.prioridade = ordem.novaPrioridade;
          });

          const totalAlteradas = response.total_alteradas || priorizacao.length;

          this.adicionarHistorico(
            'salvar',
            'Priorização salva no banco de dados',
            totalAlteradas
          );

          this.mostrarNotificacaoSucesso(`✅ ${totalAlteradas} ordens priorizadas com sucesso!`);
        } else {
          throw new Error(response.message || 'Erro ao salvar priorização');
        }

      } catch (error) {
        console.error('❌ Erro ao salvar priorização:', error);
        this.mostrarNotificacaoErro(
          error.message || 'Erro ao salvar priorização'
        );
      } finally {
        this.salvando = false;
      }
    },

    // ========== MÉTODOS DE DETALHES ==========
    verDetalhesOrdem(ordem) {
      this.ordemDetalhes = { ...ordem };
      this.dialogDetalhes = true;
    },

    // ========== MÉTODOS DE HISTÓRICO ==========
    adicionarHistorico(tipo, descricao, quantidade = 1) {
      const userStore = useUserStore();
      const usuario = userStore.objetoUsuario?.nome || 'Usuário';

      this.historicoAlteracoes.unshift({
        tipo,
        descricao,
        quantidade,
        usuario,
        timestamp: new Date()
      });

      // Manter apenas os últimos 50 registros
      if (this.historicoAlteracoes.length > 50) {
        this.historicoAlteracoes = this.historicoAlteracoes.slice(0, 50);
      }

      // Salvar no localStorage
      this.salvarHistorico();
    },

    getCorHistorico(tipo) {
      const cores = {
        'manual': 'blue',
        'automatico': 'green',
        'reset': 'orange',
        'salvar': 'success'
      };
      return cores[tipo] || 'grey';
    },

    limparHistorico() {
      this.historicoAlteracoes = [];
      this.salvarHistorico();
      this.mostrarNotificacaoInfo('Histórico limpo');
    },

    salvarHistorico() {
      try {
        localStorage.setItem('priorizacao_historico', JSON.stringify(this.historicoAlteracoes));
      } catch (error) {
        console.warn('Erro ao salvar histórico no localStorage:', error);
      }
    },

    carregarHistorico() {
      try {
        const historico = localStorage.getItem('priorizacao_historico');
        if (historico) {
          this.historicoAlteracoes = JSON.parse(historico);
        }
      } catch (error) {
        console.warn('Erro ao carregar histórico do localStorage:', error);
        this.historicoAlteracoes = [];
      }
    },

    // ========== MÉTODOS DE TEMAS ==========
    aplicarTema(tema) {
      this.temaAtual = tema.nome;

      // Remover classes de tema existentes
      document.body.classList.remove('tema-padrao', 'tema-escuro', 'tema-colorido', 'tema-minimal');

      // Adicionar nova classe de tema
      document.body.classList.add(tema.classe);

      // Salvar tema no localStorage
      localStorage.setItem('priorizacao_tema', tema.nome);

      this.mostrarNotificacaoInfo(`Tema "${tema.nome}" aplicado`);
    },

    carregarTema() {
      try {
        const temaSalvo = localStorage.getItem('priorizacao_tema');
        if (temaSalvo) {
          const tema = this.temasDisponiveis.find(t => t.nome === temaSalvo);
          if (tema) {
            this.aplicarTema(tema);
          }
        }
      } catch (error) {
        console.warn('Erro ao carregar tema:', error);
      }
    },

    // ========== MÉTODOS DE FORMATAÇÃO ==========
    getPrioridadeCor(prioridade) {
      if (prioridade <= 3) return 'error';
      if (prioridade <= 6) return 'warning';
      if (prioridade <= 10) return 'info';
      return 'grey';
    },

    getCorStatus(status) {
      const cores = {
        'pendente': 'orange',
        'em_andamento': 'blue',
        'iniciada': 'info',
        'concluida': 'green',
        'cancelada': 'red'
      };
      return cores[status] || 'grey';
    },

    formatarStatus(status) {
      const statusMap = {
        'pendente': 'Pendente',
        'em_andamento': 'Em Andamento',
        'iniciada': 'Iniciada',
        'concluida': 'Concluída',
        'cancelada': 'Cancelada'
      };
      return statusMap[status] || status;
    },

    getCorProgresso(progresso) {
      if (progresso >= 80) return 'success';
      if (progresso >= 60) return 'info';
      if (progresso >= 40) return 'warning';
      return 'error';
    },

    formatarDataSegura(data) {
      if (!data) return 'Não definido';

      try {
        const dataObj = new Date(data);
        if (isNaN(dataObj.getTime())) {
          return 'Data inválida';
        }
        return dataObj.toLocaleDateString('pt-BR');
      } catch (error) {
        return 'Data inválida';
      }
    },

    formatarDataHora(data) {
      try {
        return new Date(data).toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return 'Data inválida';
      }
    },

    calcularDiasRestantes(prazoEntrega) {
      if (!prazoEntrega) return 'Sem prazo';

      try {
        const hoje = new Date();
        const prazo = new Date(prazoEntrega);

        if (isNaN(prazo.getTime())) {
          return 'Data inválida';
        }

        const diffTime = prazo - hoje;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
          return `${Math.abs(diffDays)} dia(s) atrasado`;
        } else if (diffDays === 0) {
          return 'Vence hoje';
        } else {
          return `${diffDays} dia(s) restantes`;
        }
      } catch (error) {
        return 'Erro no cálculo';
      }
    },

    getDiasRestantesClass(prazoEntrega) {
      if (!prazoEntrega) return '';

      try {
        const hoje = new Date();
        const prazo = new Date(prazoEntrega);

        if (isNaN(prazo.getTime())) {
          return 'error--text';
        }

        const diffTime = prazo - hoje;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return 'error--text font-weight-bold';
        if (diffDays <= 3) return 'warning--text font-weight-bold';
        return 'success--text';
      } catch (error) {
        return 'error--text';
      }
    },

    // ========== MÉTODOS DE ATALHOS DE TECLADO ==========
    handleKeyboardShortcuts(event) {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 's':
            event.preventDefault();
            if (this.temAlteracoes && !this.salvando) {
              this.salvarPriorizacao();
            }
            break;
          case 'r':
            event.preventDefault();
            if (!this.processandoAcao) {
              this.resetarPriorizacao();
            }
            break;
          case 'f':
            event.preventDefault();
            this.$refs.buscaInput?.focus();
            this.mostrarFiltros = true;
            break;
          case 'h':
            event.preventDefault();
            this.dialogHistorico = true;
            break;
        }
      }
    },

    // ========== MÉTODOS DE AUTO-SAVE ==========
    iniciarAutoSave() {
      this.autoSaveInterval = setInterval(() => {
        if (this.temAlteracoes && !this.salvando) {
          this.salvarPriorizacao();
        }
      }, 30000); // Auto-save a cada 30 segundos
    },

    pararAutoSave() {
      if (this.autoSaveInterval) {
        clearInterval(this.autoSaveInterval);
        this.autoSaveInterval = null;
      }
    },

    // ========== MÉTODOS DE NOTIFICAÇÃO ==========
    mostrarNotificacaoSucesso(mensagem) {
      this.mensagemNotificacao = mensagem;
      this.tipoNotificacao = 'success';
      this.mostrarNotificacao = true;
    },

    mostrarNotificacaoErro(mensagem) {
      this.mensagemNotificacao = mensagem;
      this.tipoNotificacao = 'error';
      this.mostrarNotificacao = true;
    },

    mostrarNotificacaoInfo(mensagem) {
      this.mensagemNotificacao = mensagem;
      this.tipoNotificacao = 'info';
      this.mostrarNotificacao = true;
    }
  }
}
</script>

<style scoped>
.priorizacao-ordens {
  min-height: 400px;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

/* Cards de estatísticas melhorados */
.estatisticas-card {
  border-radius: 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.estatistica-item {
  text-align: center;
  padding: 8px;
}

.estatistica-numero {
  font-size: 2rem;
  font-weight: bold;
  line-height: 1;
}

.estatistica-label {
  font-size: 0.875rem;
  color: #666;
  margin-top: 4px;
}

.controles-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.contador-alteracoes {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 8px;
  padding: 12px;
}

.contador-numero {
  font-size: 2rem;
  font-weight: bold;
  line-height: 1;
}

.contador-label {
  font-size: 0.875rem;
  color: #666;
  margin-top: 4px;
}

/* Cards expansíveis */
.filtros-card,
.acoes-card {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.ordens-container {
  max-height: 70vh;
  overflow-y: auto;
  position: relative;
}

.ordens-draggable {
  min-height: 200px;
}

.ordem-draggable {
  transition: all 0.3s ease;
  cursor: default;
  border-radius: 12px;
  position: relative;
}

.ordem-draggable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.ordem-alterada {
  border-left: 4px solid #4CAF50;
  background: rgba(76, 175, 80, 0.05);
}

.ordem-atrasada {
  border-left: 4px solid #f44336;
  background: rgba(244, 67, 54, 0.02);
}

.ordem-urgente {
  border-top: 3px solid #ff9800;
}

.ordem-critica {
  border: 2px solid #f44336;
  background: rgba(244, 67, 54, 0.05);
}

.ordem-being-dragged {
  opacity: 0.8;
  transform: rotate(2deg);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

/* Observação */
.observacao-container {
  background: rgba(33, 150, 243, 0.05);
  padding: 8px;
  border-radius: 4px;
  border-left: 3px solid #2196f3;
}

/* Melhorias no drag handle */
.drag-handle-container {
  position: relative;
}

.drag-handle {
  cursor: grab !important;
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 4px;
}

.drag-handle:hover {
  cursor: grab !important;
  background: rgba(25, 118, 210, 0.1);
  transform: scale(1.1);
  animation: dragHint 0.5s ease-in-out infinite;
}

.drag-handle:active {
  cursor: grabbing !important;
  transform: scale(0.95);
}

.drag-handle:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* Indicador de prioridade alterada */
.prioridade-avatar {
  position: relative;
}

.prioridade-changed-indicator {
  position: absolute;
  top: -4px;
  right: -4px;
  background: white;
  border-radius: 50%;
}

/* Zonas de drop */
.drop-zone {
  height: 40px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 8px 0;
  transition: all 0.3s ease;
  opacity: 0;
  transform: scaleY(0);
  background: rgba(25, 118, 210, 0.02);
}

.drop-zone.drop-zone-active {
  opacity: 1;
  transform: scaleY(1);
  border-color: #1976d2;
  background: rgba(25, 118, 210, 0.1);
  color: #1976d2;
  font-weight: bold;
}

.drop-zone-between {
  height: 30px;
  position: relative;
  margin: 4px 0;
}

.drop-line {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background: #1976d2;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.drop-zone-active .drop-line {
  opacity: 1;
}

.drop-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #1976d2;
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.3s ease;
  white-space: nowrap;
}

.drop-zone-active .drop-indicator {
  opacity: 1;
}

/* Estilos do drag and drop */
.sortable-ghost {
  opacity: 0.3;
  background: #f5f5f5;
  transform: rotate(1deg);
}

.sortable-chosen {
  transform: scale(1.02);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
}

.sortable-drag {
  transform: rotate(3deg);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);
  z-index: 1001;
}

/* Temas personalizados */
:global(.tema-padrao) {
  --cor-primaria: #1976d2;
  --cor-secundaria: #03dac6;
  --cor-fundo: #ffffff;
  --cor-superficie: #f8f9fa;
  --cor-texto: #212121;
}

:global(.tema-escuro) {
  --cor-primaria: #bb86fc;
  --cor-secundaria: #03dac6;
  --cor-fundo: #121212;
  --cor-superficie: #1e1e1e;
  --cor-texto: #ffffff;
}

:global(.tema-escuro) .v-card {
  background-color: var(--cor-superficie) !important;
  color: var(--cor-texto) !important;
}

:global(.tema-escuro) .estatisticas-card {
  background: linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%) !important;
}

:global(.tema-colorido) {
  --cor-primaria: #e91e63;
  --cor-secundaria: #ff9800;
  --cor-fundo: #fafafa;
  --cor-superficie: #ffffff;
  --cor-texto: #212121;
}

:global(.tema-colorido) .bg-gradient-primary {
  background: linear-gradient(135deg, #e91e63 0%, #ad1457 100%) !important;
}

:global(.tema-minimal) {
  --cor-primaria: #607d8b;
  --cor-secundaria: #90a4ae;
  --cor-fundo: #ffffff;
  --cor-superficie: #f8f9fa;
  --cor-texto: #37474f;
}

:global(.tema-minimal) .bg-gradient-primary {
  background: linear-gradient(135deg, #607d8b 0%, #455a64 100%) !important;
}

/* Dialog de ajuda */
.ajuda-content h3 {
  color: #1976d2;
  border-bottom: 2px solid #e3f2fd;
  padding-bottom: 8px;
}

.ajuda-content ul {
  list-style-type: none;
  padding-left: 0;
}

.ajuda-content li {
  padding: 4px 0;
  padding-left: 20px;
  position: relative;
}

.ajuda-content li:before {
  content: "•";
  color: #1976d2;
  font-weight: bold;
  position: absolute;
  left: 0;
}

.ajuda-content kbd {
  background-color: #f5f5f5;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 2px 6px;
  font-family: monospace;
  font-size: 0.875rem;
}

/* Animações */
@keyframes dragHint {

  0%,
  100% {
    transform: translateX(0) scale(1.1);
  }

  50% {
    transform: translateX(2px) scale(1.1);
  }
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }
}

.pulse-animation {
  animation: pulse 2s infinite;
}

.fade-transition {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Melhorias no dialog */
.v-dialog .v-card {
  border-radius: 16px;
}

.posicao-preview {
  background: linear-gradient(45deg, #e3f2fd, #bbdefb);
  border: 1px solid #2196f3;
  border-radius: 8px;
}

/* Melhorias no scroll */
.smooth-scroll {
  scroll-behavior: smooth;
}

.ordens-container::-webkit-scrollbar {
  width: 8px;
}

.ordens-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.ordens-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.ordens-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Estados de foco para acessibilidade */
.ordem-draggable:focus-within {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.3);
}

/* Melhorias no menu de contexto */
.v-menu .v-list {
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* Indicadores de status melhorados */
.v-chip.ordem-status {
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.75rem;
}

/* Melhorias no progress bar */
.v-progress-linear {
  border-radius: 4px;
}

/* Hover effects melhorados */
.v-btn:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease;
}

/* Estados durante drag */
.is-dragging {
  cursor: grabbing !important;
}

.is-dragging * {
  cursor: grabbing !important;
}

/* Melhorias no slider */
.v-slider .v-slider-thumb {
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.v-slider .v-slider-track {
  border-radius: 4px;
}

/* Gaps */
.gap-1>*+* {
  margin-left: 4px;
}

.gap-2>*+* {
  margin-left: 8px;
}

.gap-4>*+* {
  margin-left: 16px;
}

/* Responsividade melhorada */
@media (max-width: 960px) {
  .estatisticas-card .v-row {
    flex-direction: column;
  }

  .controles-container {
    margin-top: 16px;
  }

  .d-flex.flex-wrap.gap-4 {
    flex-direction: column;
    align-items: flex-start;
  }

  .d-flex.flex-wrap.gap-4>*+* {
    margin-left: 0;
    margin-top: 8px;
  }

  .ordem-draggable .d-flex.align-center {
    flex-direction: column;
    align-items: flex-start;
  }

  .ordem-draggable .flex-grow-1 {
    width: 100%;
    margin-top: 12px;
  }

  .drop-zone {
    height: 35px;
  }

  .drop-indicator {
    font-size: 11px;
    padding: 3px 8px;
  }
}

@media (max-width: 600px) {
  .v-dialog {
    margin: 16px;
  }

  .v-dialog .v-card {
    margin: 0;
  }

  .drag-handle {
    margin-right: 8px;
  }

  .ordem-draggable .v-avatar {
    margin-right: 8px;
    width: 36px;
    height: 36px;
  }

  .ordem-draggable .text-h6 {
    font-size: 1rem;
  }

  .drop-zone {
    height: 30px;
    font-size: 12px;
  }

  .drop-indicator {
    font-size: 10px;
    padding: 2px 6px;
  }

  .estatistica-numero {
    font-size: 1.5rem;
  }

  .contador-numero {
    font-size: 1.5rem;
  }
}

/* Melhorias nos botões de controle */
.v-btn.v-btn--outlined {
  border-width: 1px;
  transition: all 0.2s ease;
}

.v-btn.v-btn--outlined:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Melhorias nos chips */
.v-chip {
  font-weight: 500;
}

.v-chip.v-chip--outlined {
  border-width: 1px;
}

/* Estados de loading */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* Melhorias nos alertas */
.v-alert {
  border-radius: 8px;
}

.v-alert.v-alert--variant-tonal {
  border-left: 4px solid currentColor;
}

/* Melhorias nos tooltips */
.v-tooltip .v-overlay__content {
  background: rgba(0, 0, 0, 0.8) !important;
  border-radius: 4px;
  font-size: 0.875rem;
}
</style>